import * as XLSX from "xlsx";
import type { PlantingFormData } from "@/validation/planting.schema";
import { plantingSchema } from "@/validation/planting.schema";
import type { Crop } from "seedgreen-shared/models/Crop";
import type { RanchDTO } from "seedgreen-shared/dtos/RanchDTO";
import type { LotDTO } from "seedgreen-shared/dtos/LotDTO";
import {
	parseDateFromExcelDate,
	type DateString,
} from "seedgreen-shared/types/datestring.types";
import { structuredValidate } from "@/utils/validation";

export interface ParsedRow {
	rowNumber: number;
	data: Partial<PlantingFormData>;
	errors: string[];
}

export interface ParseResult {
	success: boolean;
	rows: ParsedRow[];
	errors: string[];
}

// Map headers to field keys (supports fuzzy matching)
// Note: Validation is handled by plantingSchema, not here
const HEADERS: Record<string, keyof PlantingFormData> = {
	"planting name": "name",
	name: "name",
	ranch: "ranchId",
	farm: "ranchId",
	lots: "lotIds",
	lot: "lotIds",
	sublot: "sublot",
	crop: "cropId",
	acres: "acres",
	"wet date": "wetDate",
	"harvest date": "harvestDates",
	"harvest dates": "harvestDates",
};

/**
 * Parses an uploaded planting file (Excel or CSV) and converts it to PlantingFormData objects.
 * @returns ParseResult with parsed rows and any errors encountered
 */
export async function parsePlantingFile(
	file: File,
	crops: Crop[],
	ranches: RanchDTO[],
	lots: LotDTO[],
): Promise<ParseResult> {
	try {
		// Read the file
		const arrayBuffer = await file.arrayBuffer();

		// XLSX library will auto-detect format (Excel or CSV)
		const workbook = XLSX.read(arrayBuffer, { type: "array" });
		const rows: string[][] = XLSX.utils.sheet_to_json(
			workbook.Sheets[workbook.SheetNames[0]],
			{ header: 1, raw: false, defval: "" },
		);

		// Check if file is empty
		if (!rows.length) {
			return {
				success: false,
				rows: [],
				errors: ["Empty file"],
			};
		}

		// Map column indices to field keys
		// Strip asterisks from headers (used to mark required fields in template)
		const colMap = new Map<keyof PlantingFormData, number>();

		rows[0].forEach((header, columnIndex) => {
			const normalizedHeader = header
				.replace(/\s*\*\s*$/, "") // Remove trailing asterisks and whitespace
				.trim()
				.toLowerCase();
			const fieldKey = HEADERS[normalizedHeader];
			if (fieldKey) {
				colMap.set(fieldKey, columnIndex);
			}
		});

		// Parse data rows (skip empty rows)
		const parsed: ParsedRow[] = [];
		for (let i = 1; i < rows.length; i++) {
			if (rows[i].every((cell) => !cell?.trim())) continue;
			parsed.push(parseRow(rows[i], i, colMap, { crops, ranches, lots }));
		}

		return { success: true, rows: parsed, errors: [] };
	} catch (error) {
		return {
			success: false,
			rows: [],
			errors: [
				error instanceof Error
					? error.message
					: "Failed to parse file - please check the file format and try again",
			],
		};
	}
}

/**
 * Helper to split comma-separated values and filter out empty strings
 */
function splitAndFilterEmpty(value: string): string[] {
	return value
		.split(",")
		.map((item) => item.trim())
		.filter((item) => item.length > 0);
}

function parseRow(
	row: string[],
	idx: number,
	cols: Map<keyof PlantingFormData, number>,
	ctx: { crops: Crop[]; ranches: RanchDTO[]; lots: LotDTO[] },
): ParsedRow {
	const data: Partial<PlantingFormData> = {
		lotIds: [],
		harvestDates: [],
	};
	const errors: string[] = [];
	// Track which fields have parsing errors to avoid redundant schema errors
	const fieldsWithParseErrors = new Set<keyof PlantingFormData>();

	/**
	 * Helper to get a cell value from the row by field name.
	 * Returns the trimmed value or empty string if not found.
	 */
	const getCellValue = (fieldKey: keyof PlantingFormData): string => {
		const columnIndex = cols.get(fieldKey);
		return columnIndex !== undefined ? row[columnIndex]?.trim() : "";
	};

	// Name
	const nameValue = getCellValue("name");
	if (nameValue) {
		data.name = nameValue;
	}

	// Ranch
	const ranchValue = getCellValue("ranchId");
	if (ranchValue) {
		const ranchValueLower = ranchValue.toLowerCase();
		const ranch = ctx.ranches.find(
			(r) =>
				r.name.toLowerCase() === ranchValueLower ||
				r.fullName?.toLowerCase() === ranchValueLower,
		);
		if (ranch) {
			data.ranchId = ranch.id;
		} else {
			errors.push(`Ranch "${ranchValue}" not found`);
			fieldsWithParseErrors.add("ranchId");
		}
	}

	// Lots
	const lotsValue = getCellValue("lotIds");
	if (lotsValue) {
		const lotNames = splitAndFilterEmpty(lotsValue);
		const foundIds: number[] = [];
		const notFoundNames: string[] = [];

		lotNames.forEach((lotName) => {
			const lotNameLower = lotName.toLowerCase();
			// Try matching against name first, then fullName
			const lot =
				ctx.lots.find((l) => l.name.toLowerCase() === lotNameLower) ||
				ctx.lots.find(
					(l) => l.fullName?.toLowerCase() === lotNameLower,
				);
			if (lot) {
				foundIds.push(lot.id);
			} else {
				notFoundNames.push(lotName);
			}
		});

		if (foundIds.length) data.lotIds = foundIds;
		if (notFoundNames.length) {
			errors.push(`Lots not found: "${notFoundNames.join('", "')}"`);
			fieldsWithParseErrors.add("lotIds");
		}
	}

	// Sublot
	const sublotValue = getCellValue("sublot");
	if (sublotValue) {
		data.sublot = sublotValue;
	}

	// Crop
	const cropValue = getCellValue("cropId");
	if (cropValue) {
		const cropValueLower = cropValue.toLowerCase();
		const crop = ctx.crops.find((c) => {
			// Build the display string the same way as the UI does
			const displayString = `${c.commodity}${c.variety ? `: ${c.variety}` : ""}`;
			return displayString.toLowerCase() === cropValueLower;
		});
		if (crop) {
			data.cropId = crop.id;
		} else {
			errors.push(`Crop "${cropValue}" not found`);
			fieldsWithParseErrors.add("cropId");
		}
	}

	// Acres
	const acresValue = getCellValue("acres");
	if (acresValue) {
		const acresNumber = parseFloat(acresValue);
		if (!isNaN(acresNumber) && acresNumber > 0) {
			data.acres = acresNumber;
		} else {
			errors.push(`Invalid acres: "${acresValue}"`);
			fieldsWithParseErrors.add("acres");
		}
	}

	// Wet Date
	const wetDateValue = getCellValue("wetDate");
	if (wetDateValue) {
		const parsedDate = parseDateFromExcelDate(wetDateValue);
		if (parsedDate) {
			data.wetDate = parsedDate;
		} else {
			errors.push(`Invalid wet date: "${wetDateValue}"`);
			fieldsWithParseErrors.add("wetDate");
		}
	}

	// Harvest Dates
	const harvestDatesValue = getCellValue("harvestDates");
	if (harvestDatesValue) {
		const dateStrings = splitAndFilterEmpty(harvestDatesValue);
		const validDates: DateString[] = [];
		const invalidDates: string[] = [];

		dateStrings.forEach((dateString) => {
			const parsedDate = parseDateFromExcelDate(dateString);
			if (parsedDate) {
				validDates.push(parsedDate);
			} else {
				invalidDates.push(dateString);
			}
		});

		if (validDates.length) data.harvestDates = validDates;
		if (invalidDates.length) {
			const errorMessage = `Invalid harvest dates: "${invalidDates.join('", "')}"`;
			errors.push(errorMessage);
			fieldsWithParseErrors.add("harvestDates");
		}
	}

	// Validate using the schema - this keeps validation in sync with the form
	const validationResult = structuredValidate(plantingSchema, data);

	if (!validationResult.success) {
		// Add schema errors only for fields that didn't have parsing errors
		// This prevents redundant error messages like "Acres is required" when we already showed "Invalid acres: 'abc'"
		const schemaErrors = validationResult.errors;
		Object.entries(schemaErrors).forEach(([field, message]) => {
			if (
				message &&
				!fieldsWithParseErrors.has(field as keyof PlantingFormData)
			) {
				errors.push(message);
			}
		});
	}

	return { rowNumber: idx, data, errors };
}
